import { makeAwsSignedRequest } from '../ApiService';
import { REALM_TO_REGION } from '../../constants';

const toAwsRegion = (realm: string): string => REALM_TO_REGION[realm] ?? realm;

const unwrapAccessPayload = (value: unknown): unknown => {
    if (typeof value === 'string') {
        try {
            return unwrapAccessPayload(JSON.parse(value));
        } catch {
            return value;
        }
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    const candidate = value as Record<string, unknown>;
    if (typeof candidate.hasAccess === 'boolean') {
        return candidate;
    }

    if ('body' in candidate) {
        return unwrapAccessPayload(candidate.body);
    }

    if ('data' in candidate) {
        return unwrapAccessPayload(candidate.data);
    }

    return candidate;
};

const extractHasAccess = (value: unknown): boolean | undefined => {
    const payload = unwrapAccessPayload(value);
    if (!payload || typeof payload !== 'object') {
        return undefined;
    }

    const candidate = payload as Record<string, unknown>;
    return typeof candidate.hasAccess === 'boolean' ? candidate.hasAccess : undefined;
};

export interface UseCase {
    name: string;
    region: string;
    stage: string;
    profileName: string;
    profileId: string;
}

export interface ChangeRequest {
    changeId: string;
    useCase: string;
    stage: string;
    region: string;
    requestor: string;
    assignedReviewer: string;
    createdAt: string;
    status: string;
    fromVersion: number;
    changes?: Record<string, Record<string, Record<string, string>>>;
    proposedConfig: string;
}

export interface ChangeRequestHistoryEntry {
    changeId: string;
    useCase: string;
    stage: string;
    region: string;
    requestor: string;
    assignedReviewer: string;
    approver: string;
    status: string;
    createdAt: string;
    resolvedAt: string;
    fromVersion: number;
    toVersion: number;
    rejectionReason: string | null;
    proposedConfig?: string;
}

const appConfigApi = {
    listUseCases: async (stage: string, realm: string): Promise<UseCase[]> => {
        const awsRegion = toAwsRegion(realm);
        const result = await makeAwsSignedRequest(stage, realm, `appconfig-api/use-cases?stage=${stage}&region=${awsRegion}`, { retry: false });
        return result?.data ?? [];
    },

    getConfig: async (stage: string, realm: string, useCase: string, versionNumber?: number): Promise<{ content: string; versionNumber: number; profileName: string }> => {
        const awsRegion = toAwsRegion(realm);
        const params = new URLSearchParams({ stage, region: awsRegion, useCase });
        if (versionNumber != null) params.set('versionNumber', String(versionNumber));
        const result = await makeAwsSignedRequest(stage, realm, `appconfig-api/config?${params}`, { retry: false });
        return result?.data ?? {};
    },

    getProcessorList: async (stage: string, realm: string): Promise<Record<string, string[]>> => {
        const awsRegion = toAwsRegion(realm);
        const result = await makeAwsSignedRequest(stage, realm, `appconfig-api/processor-list?region=${awsRegion}`, { retry: false });
        const data = result?.data;
        if (!data || !Array.isArray(data)) return {};

        const map: Record<string, string[]> = {};
        for (const entry of data) {
            const processor = entry.Processor || entry.processor;
            const pms = entry.PaymentMethods || entry.paymentMethods || '';
            if (processor) {
                map[processor] = typeof pms === 'string'
                    ? pms.split(',').map((s: string) => s.trim()).filter(Boolean)
                    : [];
            }
        }
        return map;
    },

    listChangeRequests: async (stage: string, realm: string): Promise<ChangeRequest[]> => {
        const result = await makeAwsSignedRequest(stage, realm, 'appconfig-api/change-requests', { retry: false });
        return result?.data ?? [];
    },

    getHistory: async (stage: string, realm: string): Promise<ChangeRequestHistoryEntry[]> => {
        const result = await makeAwsSignedRequest(stage, realm, 'appconfig-api/change-requests/history', { retry: false });
        return result?.data ?? [];
    },

    createChangeRequest: async (stage: string, realm: string, payload: Omit<ChangeRequest, 'changeId' | 'createdAt' | 'status'>): Promise<ChangeRequest> => {
        const result = await makeAwsSignedRequest(stage, realm, 'appconfig-api/change-requests', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            retry: false,
        });
        if (result?.success === false) {
            throw new Error(result?.error?.message || 'Failed to create change request');
        }
        if (!result?.data) {
            throw new Error('Failed to create change request: no data returned');
        }
        return result.data;
    },

    approveChangeRequest: async (stage: string, realm: string, changeId: string, approver: string): Promise<void> => {
        const result = await makeAwsSignedRequest(stage, realm, `appconfig-api/change-requests/${changeId}/approve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ approver }),
            retry: false,
        });
        if (result?.success === false) {
            throw new Error(result?.error?.message || 'Failed to approve change request');
        }
    },

    rejectChangeRequest: async (stage: string, realm: string, changeId: string, approver: string, rejectionReason: string): Promise<void> => {
        const result = await makeAwsSignedRequest(stage, realm, `appconfig-api/change-requests/${changeId}/reject`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ approver, rejectionReason }),
            retry: false,
        });
        if (result?.success === false) {
            throw new Error(result?.error?.message || 'Failed to reject change request');
        }
    },

    /**
     * Returns whether the current user has access to the AppConfig Deployer dashboard.
     * The backend resolves caller identity from the Midway principalId in the authorizer
     * context (set by the API Gateway Midway authorizer). No client-side identity is needed.
     * Returns false on any error so the UI fails closed (hides the dashboard).
     */
    checkAccess: async (stage: string, realm: string): Promise<boolean> => {
        try {
            const result = await makeAwsSignedRequest(
                stage, realm, 'appconfig-api/access',
                { retry: false });
            return extractHasAccess(result) === true;
        } catch {
            return false;
        }
    },
};

export default appConfigApi;
